import {
  CarrierType,
  CarrierVisitDirection,
  CompanyResponseDto,
  OOG,
  OrderResponseDto,
  OrderStatus,
  RailTrackResponseDto,
  SealsDto,
  StowagePositionDto,
  UnitListDto,
} from '@planning/app/api'
import { IFieldConfig, csvFieldSchemaMap } from '@planning/config/csvHeaderConfig'
import { ICompanyItem } from '@planning/rt-stores/externalDriver/CompanyItem'
import { CompanyItemStore } from '@planning/rt-stores/externalDriver/CompanyItemStore'
import { RailTrackItemStore } from '@planning/rt-stores/railTrack/RailTrackItemStore'
import { formatPosition } from '@tom-ui/utils'
import _ from 'lodash'
import { computed, makeObservable, runInAction } from 'mobx'
import { openOrderFromOtherVisitsKey, orderKey } from './OrderListUploadDataStore'
import { IOrderDto, OrderListUploadViewStoreV2 } from './OrderListUploadViewStoreV2'
import { mapToUnitListDto } from './OrderMappingHelper'

export interface IOrderUpdates {
  created: IOrderDto[]
  deleted: OrderResponseDto[]
  unchanged: IOrderDto[]
  amended: IOrderAmendment[]
  deletedRestows: string[]
}

export interface IFieldDifference {
  fieldName: string
  existingValue: string
  updateValue: string
}

export type OrderIssueType = 'MatchingOrderWithDifferentTransport'
export interface IOrderIssue {
  type: OrderIssueType
  containerNumber: string
  matchingOrders: OrderResponseDto[]
}

export interface IOrderAmendment extends OrderResponseDto {
  update: UnitListDto
  differences: IFieldDifference[]
}

export interface IFieldComparatorConfig {
  key: keyof UnitListDto
  fieldName: string
  isEqual: (updateValue: any, existingValue: any) => boolean
  format: (value: any) => string
}

export class OrderListUploadComparisonStore {
  constructor(
    public parentStore: OrderListUploadViewStoreV2,
    private railTrackStore: RailTrackItemStore,
    private companyStore: CompanyItemStore,
  ) {
    makeObservable(this, {
      orderUpdates: computed,
      issues: computed,
      companyNameMap: computed,
    })
  }

  private get fieldConfigList(): IFieldConfig[] {
    const visitType = this.parentStore.vesselVisitId ? CarrierType.Vessel : CarrierType.Train
    return csvFieldSchemaMap.get(visitType) ?? []
  }

  get fieldComparatorConfig(): IFieldComparatorConfig[] {
    return this.fieldConfigList
      .filter(config => config.comparisonType !== 'none' && config.comparisonType !== 'custom')
      .map(config => {
        const dtoKey = config.inputName
        const uiLabel = config.inputName

        switch (config.comparisonType) {
          case 'string':
            return {
              key: dtoKey,
              fieldName: uiLabel,
              isEqual: isAttributeEmptyOrEqualToExisting,
              format: formatDefault,
            }
          case 'number':
            return {
              key: dtoKey,
              fieldName: uiLabel,
              isEqual: isAttributeNullOrEqualToExisting,
              format: formatNumber,
            }
          case 'boolean':
            return {
              key: dtoKey,
              fieldName: uiLabel,
              isEqual: compareBoolean,
              format: formatDefault,
            }
          case 'stringArray':
            return {
              key: dtoKey,
              fieldName: uiLabel,
              isEqual: isEqual,
              format: formatArray,
            }
          default:
            return null
        }
      })
      .filter(config => config !== null) as IFieldComparatorConfig[]
  }

  get companyNameMap() {
    return _(this.companyStore.elements).reduce(
      (map, company: ICompanyItem) => {
        map[company.id] = company.data.name
        return map
      },
      {} as Record<number, string>,
    )
  }

  get orderUpdates(): IOrderUpdates | undefined {
    if (!this.parentStore.vesselVisitId && !this.parentStore.railVisitId) return undefined

    const { dataStore, direction, listName, isRestow } = this.parentStore

    const created = dataStore.ordersFromUpdate.filter(
      update =>
        update.containerNumber &&
        !dataStore.existingOrdersByContainerNumber.has(
          orderKey(update.containerNumber, direction, listName),
        ),
    )

    created.filter(o => o.containerNumber).forEach(this.linkToAdjacentOrder)

    const { amended, unchanged } = this.findAmendedAndUnchangedOrders()

    const deleted = dataStore.nonRestowOrdersFromVisit.filter(
      existing =>
        existing.containerNumber &&
        existing.direction === direction &&
        existing.status === OrderStatus.Open &&
        (existing.listName ?? OrderListUploadViewStoreV2.defaultListName) === listName &&
        !dataStore.updatingOrdersByContainerNumber.has(existing.containerNumber),
    )
    const remainOnboard = dataStore.remainOnBoard.map(r => r.containerNumber)

    const deletedRestows = !isRestow
      ? dataStore.restowInboundOrdersFromVisit
          .filter(
            existing =>
              existing.status === OrderStatus.Open &&
              !remainOnboard.includes(existing?.containerNumber ?? '') &&
              existing.containerNumber?.length,
          )
          .map(item => item.containerNumber!)
      : []

    return {
      created,
      deleted,
      amended,
      unchanged,
      deletedRestows,
    }
  }

  private findAmendedAndUnchangedOrders = () => {
    const amended: IOrderAmendment[] = []
    const unchanged: UnitListDto[] = []

    const { dataStore, direction, listName } = this.parentStore

    dataStore.ordersFromUpdate.forEach(update => {
      if (!update.containerNumber) return

      if (
        dataStore.existingOrdersByContainerNumber.has(
          orderKey(update.containerNumber, direction, listName),
        )
      ) {
        const existing = dataStore.existingOrdersByContainerNumber.get(
          orderKey(update.containerNumber, direction, listName),
        )[0]

        const amendment = this.mapToAmendment(update, existing)

        if (amendment.differences.length) amended.push(amendment)
        else unchanged.push(update)
      }
    })

    return { amended, unchanged }
  }

  // We might want to reconsider this logic
  // upload inbound -> usual linking
  //   * no service orders exist since only created orders are considered
  //   * only unlinked outbounds are considered - hence also not connected to service orders
  // upload outbound
  //   * inbound does not reference service orders - usual linking
  //   * inbound has service orders - link it or not?
  //      + inbound full with StrippingOrder: link if outbound is empty?
  //      + inbound empty with StuffingOrder: link if outbound is full?
  // ~~ For now we do not link orders referencing Stripping-/StuffingOrders
  private linkToAdjacentOrder = (createdOrder: UnitListDto) => {
    const { dataStore, direction } = this.parentStore
    const { openOrdersFromOtherVisitsByContainerNumber } = dataStore

    const oppositeDirection =
      direction === CarrierVisitDirection.Inbound
        ? CarrierVisitDirection.Outbound
        : CarrierVisitDirection.Inbound

    const lookupKey = openOrderFromOtherVisitsKey(createdOrder.containerNumber, oppositeDirection)
    if (openOrdersFromOtherVisitsByContainerNumber.has(lookupKey)) {
      const openOrders = openOrdersFromOtherVisitsByContainerNumber.get(lookupKey).filter(o => {
        if (!o.hasServiceOrders) {
          return (createdOrder.isEmpty ?? false) === o.isEmpty
        }

        return false
      })

      if (openOrders.length) {
        runInAction(() => {
          createdOrder.linkedOrderId = openOrders[0].id
        })
      }
    }
  }

  get issues() {
    const issues = [] as IOrderIssue[]
    const { dataStore, direction } = this.parentStore

    dataStore.ordersFromUpdate.forEach(o => {
      if (
        dataStore.existingUpdateRelatedOrdersByContainerNumber.has(
          orderKey(o.containerNumber, direction),
        )
      ) {
        const openOrdersWithDifferentVisit = dataStore.existingUpdateRelatedOrdersByContainerNumber
          .get(orderKey(o.containerNumber, direction))
          .filter(
            o =>
              o.status === OrderStatus.Open &&
              o.direction === this.parentStore.direction &&
              o.carrierVisitId !==
                (this.parentStore.vesselVisitId || this.parentStore.railVisitId) &&
              o.carrierVisitType === CarrierType.Vessel,
          )

        if (openOrdersWithDifferentVisit.length) {
          issues.push({
            type: 'MatchingOrderWithDifferentTransport',
            containerNumber: o.containerNumber,
            matchingOrders: openOrdersWithDifferentVisit,
          })
        }
      }
    })

    return issues
  }

  mapToAmendment = (update: UnitListDto, existing: OrderResponseDto): IOrderAmendment => {
    const { customer } = this.parentStore
    return {
      ...existing,
      update: { ...update, id: existing.id },

      differences: findDifferences(
        update,
        mapToUnitListDto(existing, this.companyNameMap),
        existing.customerName ?? '',
        customer,
        this.fieldComparatorConfig,
        this.railTrackStore.activeRailTracks,
        _(this.companyStore.elements).values().value(),
      ),
    }
  }
}

export const serialize = (outOfGauge?: OOG[] | null): string[] =>
  outOfGauge
    ?.slice()
    .sort((a, b) => (a.direction && b.direction ? a.direction?.localeCompare(b.direction) : 0))
    .map(oog => `${oog.direction?.toLocaleLowerCase()}: ${oog.measurement}`) ?? []

export const isEqualStowage = (
  left?: StowagePositionDto | undefined,
  right?: StowagePositionDto | undefined,
): boolean => left?.bay === right?.bay && left?.row === right?.row && left?.tier === right?.tier

export const isEqual = (left: string[], right: string[]): boolean =>
  left?.length === right?.length && left.every(leftElem => right.includes(leftElem))

export const isAttributeNullOrEqualToExisting = (
  attribute?: string | number | null,
  comparison?: string | number | null,
): boolean => {
  const normAttribute = attribute ?? 0
  const normComparison = comparison ?? 0
  return normAttribute === normComparison
}

export const isAttributeEmptyOrEqualToExisting = (
  attribute?: string | null,
  comparison?: string | null,
): boolean => {
  const normAttribute = attribute ?? ''
  const normComparison = comparison ?? ''
  return normAttribute.trim() === normComparison.trim()
}

/**
 * Determines if both the `updated` and `existing` company identifiers refer to the same company
 *
 * A match is found if both `updated` and `existing` correspond to the same company's `name`,
 * `shortName`, or are included in the company's `alternativeNames` array.
 *
 * @param updated - The updated company identifier (name, shortName, or alternative name) to check.
 * @param existing - The existing company identifier (name, shortName, or alternative name) to check.
 * @returns The matching company object if both identifiers refer to the same company; otherwise, `undefined`.
 */
export const isCompanyEqualToExisting = (
  companies: ICompanyItem[],
  updated?: string | null,
  existing?: string | null,
): boolean => {
  if (!updated && !existing) return true

  if (updated === existing) return true

  const existingCompany = companies.find(company => nameLater(company, existing ?? ''))

  if (!existingCompany) return false

  return nameLater(existingCompany, updated ?? '')
}

const nameLater = (company: ICompanyItem, identifier: string) => {
  const names = [
    company.data.name,
    company.data.shortName,
    ...(company.data.alternativeNames ?? []),
  ].filter(Boolean)

  return names.includes(identifier)
}

export const serializeSeals = (seals?: SealsDto[] | null): string[] =>
  seals
    ?.map(seal => {
      if (!seal.number) return null
      return seal.type ? `${seal.type}: ${seal.number}` : seal.number
    })
    ?.filter((sealStr): sealStr is string => !!sealStr)
    ?.sort() ?? []

export const compareBoolean = (u?: boolean | null, e?: boolean | null): boolean => {
  return (u ?? false) === (e ?? false)
}

export const formatDefault = (v: any): string => v?.toString() ?? ''
export const formatNumber = (v: any): string => (v ?? 0).toString()
export const formatArray = (v: string[]): string => v?.join(', ') ?? ''

export const findDifferences = (
  update: UnitListDto,
  existing: UnitListDto,
  existingCustomerName: string | undefined,
  customer: CompanyResponseDto | undefined,
  fieldComparatorConfig: readonly IFieldComparatorConfig[],
  activeRailTracks: RailTrackResponseDto[],
  companies: ICompanyItem[],
): IFieldDifference[] => {
  const differences = [] as IFieldDifference[]

  for (const config of fieldComparatorConfig) {
    const updateValue = update[config.key]
    const existingValue = existing[config.key]

    if (!config.isEqual(updateValue, existingValue)) {
      differences.push({
        fieldName: config.fieldName,
        existingValue: config.format(existingValue),
        updateValue: config.format(updateValue),
      })
    }
  }

  if (!isAttributeEmptyOrEqualToExisting(customer?.name, existingCustomerName)) {
    differences.push({
      fieldName: 'customerName',
      existingValue: existingCustomerName ?? '',
      updateValue: customer?.name ?? '',
    })
  }

  const currentGrossWeight = existing.grossWeight ?? 0
  const updateGrossWeight = update.grossWeight ?? update.vgm ?? 0
  if (!isAttributeNullOrEqualToExisting(updateGrossWeight, currentGrossWeight)) {
    differences.push({
      fieldName: 'grossWeight',
      existingValue: currentGrossWeight.toString(),
      updateValue: updateGrossWeight.toString(),
    })
  }

  const currentVgm = existing.vgm ?? 0
  const updateVgm = update.vgm ?? 0
  if (!isAttributeNullOrEqualToExisting(updateVgm, currentVgm)) {
    differences.push({
      fieldName: 'vgm',
      existingValue: currentVgm.toString(),
      updateValue: updateVgm.toString(),
    })
  }

  if (!isAttributeEmptyOrEqualToExisting(update.railTrackId, existing.railTrackId)) {
    const currentTrack = activeRailTracks.find(x => x.id === existing.railTrackId)
    const nextTrack = activeRailTracks.find(x => x.id === update.railTrackId)

    differences.push({
      fieldName: 'railTrack',
      existingValue: currentTrack?.name ?? '',
      updateValue: nextTrack?.name ?? '',
    })
  }

  if (!isAttributeEmptyOrEqualToExisting(update.waggon, existing.waggon)) {
    differences.push({
      fieldName: 'railcarName',
      existingValue: existing.waggon ?? '',
      updateValue: update.waggon ?? '',
    })
  }

  if (!isEqual(serialize(update.outOfGauge), serialize(existing.outOfGauge))) {
    differences.push({
      fieldName: 'oog',
      existingValue: serialize(existing.outOfGauge)?.join(', ') ?? '',
      updateValue: serialize(update.outOfGauge)?.join(', ') ?? '',
    })
  }

  if (!isEqualStowage(update.stowagePosition, existing.stowagePosition)) {
    differences.push({
      fieldName: 'stowagePosition',
      existingValue: existing.stowagePosition ? formatPosition(existing.stowagePosition) : '',
      updateValue: update.stowagePosition ? formatPosition(update.stowagePosition) : '',
    })
  }

  const updateSeals = serializeSeals(update.seals)
  const existingSeals = serializeSeals(existing.seals)
  if (!isEqual(updateSeals, existingSeals)) {
    differences.push({
      fieldName: 'Seals',
      existingValue: formatArray(existingSeals),
      updateValue: formatArray(updateSeals),
    })
  }

  // Companies
  if (!isCompanyEqualToExisting(companies, update.operator, existing.operator)) {
    differences.push({
      fieldName: 'operator',
      existingValue: existing.operator ?? '',
      updateValue: update.operator ?? '',
    })
  }

  if (!isCompanyEqualToExisting(companies, update.shipper, existing.shipper)) {
    differences.push({
      fieldName: 'shipper',
      existingValue: existing.shipper ?? '',
      updateValue: update.shipper ?? '',
    })
  }

  if (!isCompanyEqualToExisting(companies, update.forwarder, existing.forwarder)) {
    differences.push({
      fieldName: 'forwarder',
      existingValue: existing.forwarder ?? '',
      updateValue: update.forwarder ?? '',
    })
  }

  if (!isCompanyEqualToExisting(companies, update.consignee, existing.consignee)) {
    differences.push({
      fieldName: 'consignee',
      existingValue: existing.consignee ?? '',
      updateValue: update.consignee ?? '',
    })
  }

  return differences
}
